<script>
import tableComp from '@/compositions/modules/table'
import SrTblColumn from '@/components/SrTblColumn'
import { components } from '@libs/ui'
import { useStore } from 'vuex'
import { toRefs, reactive, computed, ref } from 'vue'
import { getReferrerList, updateRemark, homeOverview, openAcctConversionTrend, payTrend, transinTrend } from '@/api/modules/auth'
import { dataUtils, vab } from '@libs/utils'
import { useI18n } from "@/plugins/i18n"

import SimpleLineChart from '@/components/SimpleLineChart'

export default {
  name: 'TableControl',
  components: { SrTblColumn, SrTblPagination: components.SrTblPagination, SimpleLineChart },
  setup() {
    const { t } = useI18n()
    const store = useStore()
    const registerInfo = computed(() => ({
      ...store.state.auth.registerInfo,
    }))
    
    const remarkFormRef = ref(null)
    
    const remarkRules = {
      remark: [
        { required: true, message: t('dashboard.remarkDialog.validate.remarkRequired'), trigger: 'blur' }
      ]
    }
    
    const state = reactive({
      emptyImg: require('@/assets/img/dashboard/empty.png'),
      incomeDateIcon: require('@/assets/img/income-date-icon.png'),
      now: dataUtils.moment().format('YYYY/MM/DD'),
      rebateDateStart: dataUtils.formatDate(registerInfo?.value?.rebateDateList?.[0] || '', 'YYYY/MM/DD'),
      rebateDateEnd: dataUtils.formatDate(registerInfo?.value?.rebateDateList?.[1] || '', 'YYYY/MM/DD'),
      remarkDialog: {
        visible: false,
        inviteCode: '',
        remark: ''
      },
      // 总体概览相关
      days: 7,
      overviewLoading: false,
      overviewData: {
        incomeUsdAmt: 0,
        openAcctCount: 0,
        registerCount: 0,
        transinUsdAmt: 0
      },
      // 图表数据
      chartLoading: false,
      conversionChartData: [],
      payChartData: [],
      transinChartData: []
    })
    const scopedMethods = {
      dateDiff: dataUtils.momentDiff,
      formatDate: dataUtils.formatDate,
      dayjs: dataUtils.moment,
      // 初始化数据
      initData: async () => {
        await scopedMethods.fetchOverviewData()
        await scopedMethods.fetchChartData()
      },
      // 处理复制
      handleCopy: (data) => {
        const oInput = document.createElement('input')
        oInput.value = data
        document.body.appendChild(oInput)
        oInput.select()
        document.execCommand('Copy')
        oInput.remove()
        vab.baseMessage(t('global.content.text_12'), 'success')
      },
      // 打开修改备注弹窗
      openRemarkDialog: (row) => {
        state.remarkDialog.inviteCode = row.inviteCode
        state.remarkDialog.remark = row.remark || ''
        state.remarkDialog.visible = true
      },
      // 关闭修改备注弹窗
      closeRemarkDialog: () => {
        state.remarkDialog.visible = false
        state.remarkDialog.inviteCode = ''
        state.remarkDialog.remark = ''
      },
      // 切换天数
      changeDays: async (days) => {
        state.days = days
        await scopedMethods.fetchOverviewData()
        await scopedMethods.fetchChartData()
      },
      // 获取总体概览数据
      fetchOverviewData: async () => {
        state.overviewLoading = true
        try {
          const res = await homeOverview({
            days: state.days,
            userId: store.state.user.userId
          })
          state.overviewData = {
            incomeUsdAmt: res.incomeUsdAmt || 0,
            openAcctCount: res.openAcctCount || 0,
            registerCount: res.registerCount || 0,
            transinUsdAmt: res.transinUsdAmt || 0
          }
        } catch (error) {
          console.error('获取总体概览数据失败:', error)
        } finally {
          state.overviewLoading = false
        }
      },
      // 获取图表数据
      fetchChartData: async () => {
        state.chartLoading = true
        try {
          // 开户转化率
          const conversionRes = await openAcctConversionTrend({
            days: state.days,
            userId: store.state.user.userId
          })
          state.conversionChartData = conversionRes.points || []
          
          // 支付交易趋势
          const payRes = await payTrend({
            days: state.days,
            userId: store.state.user.userId
          })
          state.payChartData = payRes.points || []
          
          // 收款交易趋势
          const transinRes = await transinTrend({
            days: state.days,
            userId: store.state.user.userId
          })
          state.transinChartData = transinRes.points || []
        } catch (error) {
          console.error('获取图表数据失败:', error)
        } finally {
          state.chartLoading = false
        }
      }
    }
    // 先初始化表格，获取 handleSearch 方法
    const tableInstance = initTable()
    
    // 修改 submitRemark，使用 tableInstance 中的 handleSearch
    scopedMethods.submitRemark = async () => {
      try {
        await remarkFormRef.value.validate()
        await updateRemark({
          inviteCode: state.remarkDialog.inviteCode,
          remark: state.remarkDialog.remark
        })
        vab.baseMessage(t('global.content.text_5'), 'success')
        scopedMethods.closeRemarkDialog()
        // 刷新列表
        tableInstance.handleSearch()
      } catch (error) {
        console.error('error', error)
      }
    }
    
    // 页面初始化时调用数据获取方法
    scopedMethods.initData()
    return { ...toRefs(tableInstance), ...toRefs(state), registerInfo, ...scopedMethods, t, remarkFormRef, remarkRules }
  }
}

const initTable = () => {
  const { createTable } = tableComp()
  const { t } = useI18n()
  const columnsTemp = [
    { prop: 'linkedUserName', label: t('dashboard.share.content.text_3'), align: 'left', minWidth: '200' },
    { slot: 'inviteCode', label: t('dashboard.table.text_7'),align: 'left', minWidth: '200' },
    { slot: 'platform', label: t('dashboard.share.content.text_4'),align: 'left', minWidth: '600' },
    { slot: 'remark', label: t('dict.label.otherInformation'), align: 'left', minWidth: '500', tooltip: 'off' },
  ]
  const tbl = createTable({ columns: columnsTemp })
  const searchInput = ref('')

  const fetchData = async () => {
    tbl.data.loading = true
    tbl.data.list = []
    try {
      const res = await getReferrerList({
        inviteCode: searchInput.value,
        pageInfo: {
          current: tbl.pagination.currentPage,
          size: tbl.pagination.pageSize
        }
      })
      tbl.data.list = res.list.map(item => ({
        ...item,
        baseUrl: process?.env?.NODE_ENV === 'production' ? 'https://pro.sunrate.com/#/register?inviteCode=' : 'https://uatpro.91xunhui.cn/#/register?inviteCode='
      }))
      tbl.pagination.total = res.pageInfo.total
    } finally {
      tbl.data.loading = false
    }
  }

  // 搜索函数
  const handleSearch = () => {
    tbl.pagination.currentPage = 1
    fetchData()
  }

  fetchData()
  return { tbl, fetchData, searchInput, handleSearch }
}
</script>

<template>
  <div class="table-container">
    <div class="income-date-wrap" v-if="registerInfo.kycReviewStatus === 'CF' && registerInfo.rebateDateList?.length">
      <img class="income-date-icon" :src="incomeDateIcon" />
      <div>
        {{ t('dashboard.dialog.content.text_7', { start: rebateDateStart || '未设置', end: rebateDateEnd || '未设置' }) }}
        <span style="color: #E15C46"
          v-if="registerInfo.rebateDateList?.filter(item => item)?.length === 2 && dateDiff(now, rebateDateStart, 'day') >= 0 && dateDiff(rebateDateEnd, now, 'day') <= 30">({{
            dateDiff(rebateDateEnd, now, 'day') >= 0 ? dateDiff(rebateDateEnd, now, 'day') === 0 ?
              t('dashboard.dialog.content.text_8') : t('dashboard.dialog.content.text_9', {
                day: dateDiff(rebateDateEnd,
                  now, 'day')
              }) : t('dashboard.dialog.content.text_10') }})</span>
      </div>
    </div>
    
    <!-- 总体概览 -->
    <div class="overview-container">
      <div class="overview-header">
        <div class="overview-title">{{ t('dashboard.overview.title') }}</div>
        <!-- <div class="overview-subtitle">{{ t('dashboard.overview.timeHint') }}</div> -->
        <div class="days-selector">
          <el-button 
            :class="{ active: days === 7 }" 
            @click="changeDays(7)">{{ t('dashboard.overview.days7') }}</el-button>
          <el-button 
            :class="{ active: days === 30 }" 
            @click="changeDays(30)">{{ t('dashboard.overview.days30') }}</el-button>
          <el-button 
            :class="{ active: days === 90 }" 
            @click="changeDays(90)">{{ t('dashboard.overview.days90') }}</el-button>
        </div>
      </div>
      
      <!-- 统计卡片 -->
      <div class="stats-cards" v-loading="overviewLoading">
        <div class="stat-card card-1">
          <div class="stat-label">{{ t('dashboard.overview.registerCount') }}</div>
          <div class="stat-value">{{$filters.currency(overviewData.registerCount, '', '', 'amount')}}</div>
        </div>
        <div class="stat-card card-2">
          <div class="stat-label">{{ t('dashboard.overview.openAcctCount') }}</div>
          <div class="stat-value">{{$filters.currency(overviewData.openAcctCount, '', '', 'amount')}}</div>
        </div>
        <div class="stat-card card-3">
          <div class="stat-label">{{ t('dashboard.overview.transinUsdAmt') }}</div>
          <div class="stat-value">{{$filters.currency(overviewData.transinUsdAmt, '', '', 'amount')}}</div>
        </div>
        <div class="stat-card card-4">
          <div class="stat-label">{{ t('dashboard.overview.incomeUsdAmt') }}</div>
          <div class="stat-value">{{$filters.currency(overviewData.incomeUsdAmt, '', '', 'amount')}}</div>
        </div>
      </div>
      
      <!-- 图表区域 -->
      <div class="charts-container" v-loading="chartLoading">
        <!-- <div class="chart-card">
          <div class="chart-title">{{ t('dashboard.overview.conversionRate') }}</div>
          <div class="chart-content">
            <simple-line-chart 
              :data="conversionChartData" 
              :xKey="'date'" 
              :yKey="'conversionRate'"
              :yMax="100"
              :yUnit="'%'"
              :color="'#5B8FF9'">
            </simple-line-chart>
          </div>
        </div> -->
        <div class="chart-card">
          <div class="chart-title">{{ t('dashboard.overview.transinTrend') }} /USD</div>
          <div class="chart-content">
            <simple-line-chart 
              :data="transinChartData" 
              :xKey="'date'" 
              :yKey="'amount'"
              :yUnit="''"
              :color="'#5B8FF9'"
              :width="650"
              :height="150">
            </simple-line-chart>
          </div>
        </div>
        <div class="chart-card">
          <div class="chart-title">{{ t('dashboard.overview.payTrend') }} /USD</div>
          <div class="chart-content">
            <simple-line-chart 
              :data="payChartData" 
              :xKey="'date'" 
              :yKey="'amount'"
              :yUnit="''"
              :color="'#5B8FF9'"
              :width="650"
              :height="150">
            </simple-line-chart>
          </div>
        </div>
      </div>
    </div>
    
    <div style="padding: 12px 12px;border-radius: 4px;position: relative;">
      <div class="search-container">
        <div class="h1" style="margin-bottom: 10px;">{{ t('dashboard.share.title.text_1') }}</div>
        <div class="search-wrap">
          <el-input class="search-input" v-model="searchInput" :placeholder="t('dashboard.share.title.search')" />
          <el-button type="primary" class="search-btn" @click="handleSearch">
            <el-icon style="margin-right:5px">
              <Search />
            </el-icon>
            {{ t('client.content.text_1') }}
          </el-button>
        </div>
        <div class="search-hint">{{ t('dashboard.share.content.text_1') }}</div>

        <img class="search-bg" src="@/assets/img/dashboard/search-bg.png" alt="" />
      </div>
      <el-table row-class-name="row-con" :data="tbl.data.list" v-loading="tbl.data.loading">
        <sr-tbl-column :columns="tbl.columns">
          <template #inviteCode="{ row }">
            <div>{{ row.inviteCode }} <span class="operation"
                @click="handleCopy(row.inviteCode)">{{ t('global.content.text_11') }}</span></div>
          </template>
          <template #platform="{ row }">
            <div>{{ row.baseUrl }}{{ row.inviteCode }} <span class="operation"
                @click="handleCopy(row.baseUrl + row.inviteCode)">{{ t('global.content.text_11') }}</span></div>
          </template>
          <template #remark="{ row }">
            <el-tooltip :content="row.remark" placement="top" :disabled="!row.remark" popper-class="remark-tooltip">
              <div class="remark-text">
                <span class="edit-icon" @click="openRemarkDialog(row)">
                  <el-icon><Edit /></el-icon>
                </span> {{ row.remark }}
              </div>
            </el-tooltip>
          </template>
        </sr-tbl-column>
        <template #empty>
          <div class="empty-wrap">
            <img :src="emptyImg" alt="">
            {{ t('dashboard.dialog.content.text_6') }}
          </div>
        </template>
      </el-table>
      <!-- 修改备注弹窗 -->
      <el-dialog :title="t('dashboard.remarkDialog.title')" v-model="remarkDialog.visible" width="500px">
        <el-form :model="remarkDialog" :rules="remarkRules" ref="remarkFormRef" label-width="100px" >
          <el-form-item :label="t('dict.label.otherInformation')" prop="remark">
            <el-input type="textarea" v-model="remarkDialog.remark" :rows="4" maxlength="255" :placeholder="t('dashboard.remarkDialog.placeholder')"></el-input>
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="closeRemarkDialog">{{ t('dashboard.remarkDialog.cancelBtn') }}</el-button>
          <el-button type="primary" @click="submitRemark">{{  t('dashboard.remarkDialog.submitBtn') }}</el-button>
        </template>
      </el-dialog>
      <sr-tbl-pagination v-model:current-page="tbl.pagination.currentPage" v-model:page-size="tbl.pagination.pageSize"
        :total="tbl.pagination.total" @fetchData="fetchData">
      </sr-tbl-pagination>
    </div>
  </div>
</template>

<style lang="scss" scoped rel="stylesheet/scss">
.table-container {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  background-color: #fff;
  border-radius: 4px;
  border: 1px solid #FFF;

  position: relative;
  
  .overview-container {
    padding: 20px;
    margin-bottom: 20px;
    background: #fff;
    border-radius: 4px;
    border: 1px solid #eee;
    
    .overview-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 20px;
      
      .overview-title {
        font-size: 18px;
        font-weight: 600;
        color: #303133;
      }
      
      .overview-subtitle {
        font-size: 12px;
        color: #999;
        margin-left: 10px;
      }
      
      .days-selector {
        display: flex;
        
        :deep(.el-button) {
          width: 80px;
          height: 32px;
          border-radius: 4px;
          font-size: 12px;
          
          &.active {
            background: #00ACEA;
            border-color: #00ACEA;
            color: #fff;
          }
        }
      }
    }
    
    .stats-cards {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 20px;
      margin-bottom: 20px;
      
      .stat-card {
        padding: 30px 20px;
        border-radius: 8px;
        
        &.card-1 {
          background: #F0FAF4;
        }
        
        &.card-2 {
          background: #FFF5F0;
        }
        
        &.card-3 {
          background: #F0F5FF;
        }
        
        &.card-4 {
          background: #FFFBEB;
        }
        
        .stat-label {
          font-size: 14px;
          color: #606266;
          margin-bottom: 10px;
        }
        
        .stat-value {
          font-size: 24px;
          font-weight: 600;
          color: #303133;
        }
      }
    }
    
    .charts-container {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
      
      .chart-card {
        padding: 10px 16px;
        background: #FAFBFC;
        border-radius: 8px;
        
        .chart-title {
          font-size: 14px;
          color: #303133;
          font-weight: 500;
        }
        
        .chart-content {
          width: 100%;
          height: auto;
          position: relative;
        }
      }
    }
  }

  .income-date-wrap {
    background-color: #fdf5e4;
    display: flex;
    align-items: center;
    padding: 19px 20px;
    font-size: 14px;
    font-weight: 400;
    color: #BB985F;

    .income-date-icon {
      width: 19px;
      display: block;
      height: auto;
      margin-right: 11px;
    }
  }

  .empty-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 160px auto;
    box-sizing: border-box;

    &>img {
      width: 190px;
      height: auto;
      display: block;
      margin-bottom: 38px;
    }
  }

  :deep(.row-con) {
    height: 57px;
  }
}

.operation {
  text-align: center;
  font-size: 12px;
  color: #00ACEA;
  padding: 4px;
  letter-spacing: 0;
  background: rgba(0, 172, 234, 0.12);
  border-radius: 3px;
  box-sizing: border-box;
  margin-left: 10px;
  cursor: pointer;
  white-space: nowrap;
}

.remark-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.edit-icon {
  margin-left: 10px;
  color: #00ACEA;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    color: #0088cc;
  }
}

.search-container {
  background: #F4F9FF;
  // height: 130px;
  overflow: hidden;
  position: relative;
  padding: 15px 15px;
  border-radius: 4px;
  margin-bottom: 10px;

  .search-input {
    width: 650px;
    height: 48px;
    border-radius: 10px;
    box-sizing: border-box;
    margin-right: 10px;
  }

  .search-btn {
    height: 48px;
  }

  .search-bg {
    position: absolute;
    right: 10px;
    top: 10px;
    width: 270px;
    height: auto;
  }

  .search-hint {
    font-weight: 400;
    font-size: 12px;
    margin-top: 8px;
    color: #606266;
  }
}
</style>
<style>
  .remark-tooltip {
    max-width: 500px;
    white-space: normal;
    word-break: break-all;
  }
</style>
